import { useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';

const STORAGE_KEY_VIEWS     = 'fv_page_views';
const STORAGE_KEY_TIME      = 'fv_time_seconds';
const STORAGE_KEY_DISMISSED = 'fv_soft_login_dismissed_at';
const VIEWS_THRESHOLD       = 3;
const TIME_THRESHOLD_SECS   = 90;
const DISMISS_COOLDOWN_MS   = 24 * 60 * 60 * 1000;

export function useSoftLogin() {
  const { isSignedIn, isLoaded } = useUser();
  const [showModal,   setShowModal]   = useState(false);
  const [triggerKind, setTriggerKind] = useState(null);

  // Count this page load as one view
  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    const views = parseInt(localStorage.getItem(STORAGE_KEY_VIEWS) || '0', 10);
    localStorage.setItem(STORAGE_KEY_VIEWS, String(views + 1));
  }, [isLoaded, isSignedIn]);

  // Tick active-tab time every second
  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      const t = parseInt(localStorage.getItem(STORAGE_KEY_TIME) || '0', 10);
      localStorage.setItem(STORAGE_KEY_TIME, String(t + 1));
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isLoaded, isSignedIn]);

  // Evaluate thresholds every 5 seconds
  useEffect(() => {
    if (!isLoaded || isSignedIn) return;

    const check = () => {
      const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEY_DISMISSED) || '0', 10);
      if (Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

      const views = parseInt(localStorage.getItem(STORAGE_KEY_VIEWS) || '0', 10);
      const time  = parseInt(localStorage.getItem(STORAGE_KEY_TIME)  || '0', 10);

      if (views >= VIEWS_THRESHOLD || time >= TIME_THRESHOLD_SECS) {
        const kind = views >= VIEWS_THRESHOLD ? 'views' : 'time';
        setTriggerKind(kind);
        setShowModal(true);
        window.__posthog?.capture('soft_login_shown', {
          trigger:          kind,
          views_at_trigger: views,
          time_at_trigger:  time,
        });
      }
    };

    check();
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, [isLoaded, isSignedIn]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY_DISMISSED, String(Date.now()));
    window.__posthog?.capture('soft_login_dismissed', { trigger: triggerKind });
    setShowModal(false);
  };

  const triggerImmediate = () => {
    if (isSignedIn) return;
    const views = parseInt(localStorage.getItem(STORAGE_KEY_VIEWS) || '0', 10);
    const time  = parseInt(localStorage.getItem(STORAGE_KEY_TIME)  || '0', 10);
    setTriggerKind('pro_click');
    setShowModal(true);
    window.__posthog?.capture('soft_login_shown', {
      trigger:          'pro_click',
      views_at_trigger: views,
      time_at_trigger:  time,
    });
  };

  return { showModal, dismiss, triggerImmediate, triggerKind };
}
